import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Monitor, RotateCcw } from 'lucide-react';
import { useCampaignOverviewQuery } from '@/features/campaigns/api/campaignDetailApi';
import { useCan } from '@/shared/hooks/useCan';
import { TABS, type TabKey } from './campaignDetailPage.constants';
import { fmtDate } from './campaignDetailPage.utils';
import { OverviewTab } from './tabs/OverviewTab';
import { ScreensTab } from './tabs/ScreensTab';
import { ProgressTab } from './tabs/ProgressTab';
import { AnalyticsTab } from './tabs/AnalyticsTab';
import { RootState } from '@/shared/store';
import { useSelector } from 'react-redux';
import { STATUS_COLORS } from '../../utils/campaignListUtils';

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const navigate = useNavigate();
  const canRelaunch = useCan('relaunch-campaign');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabKey) || 'overview';
  const setTab = (t: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const overviewQ = useCampaignOverviewQuery(id);
  const campaign = overviewQ.data?.data;

  if (overviewQ.isPending) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-7 w-7 animate-spin text-[var(--dash-brand)]" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-3 py-24 text-center">
        <Monitor className="h-10 w-10 text-[var(--dash-muted)] opacity-30" />
        <p className="m-0 text-sm font-semibold text-[var(--dash-ink)]">
          Campaign not found
        </p>
        <button
          type="button"
          onClick={() => navigate('/campaigns')}
          className="rounded-full bg-[var(--dash-brand)] px-5 py-2.5 text-sm font-semibold text-white"
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  const status = campaign.status?.toUpperCase() || 'PENDING';
  const statusStyle = STATUS_COLORS[status] || STATUS_COLORS.PENDING;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 pb-8 sm:gap-6">
      {/* Hero header */}
      <header className="overflow-hidden rounded-[24px] border border-black/[0.06] bg-white/90 shadow-[0_10px_36px_-22px_rgba(15,23,42,0.22)] backdrop-blur-sm">
        <div className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-5">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <button
              type="button"
              onClick={() => navigate('/campaigns')}
              className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F2F4F7] text-[var(--dash-ink)] transition hover:bg-[#E8ECF2]"
              aria-label="Back to campaigns"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1 pt-0.5">
              <h1
                className="m-0 text-[22px] leading-tight font-semibold tracking-[-0.03em] text-[var(--dash-ink)] break-words [overflow-wrap:anywhere] sm:text-[26px]"
                title={campaign.campaignName}
              >
                {campaign.campaignName || 'Campaign'}
              </h1>
              <p className="m-0 mt-1.5 text-[13px] text-[var(--dash-muted)]">
                <span className="font-medium text-[var(--dash-ink)]/80">
                  {campaign.orderId || '—'}
                </span>
                <span className="mx-1.5 opacity-40">·</span>
                {fmtDate(campaign.startDate)} – {fmtDate(campaign.endDate)}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 self-start sm:self-center">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-[0.04em] uppercase ${statusStyle.bg} ${statusStyle.text}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusStyle.dotBg}`} />
              {status.replaceAll('_', ' ')}
            </span>
            {canRelaunch && status === 'PUBLISHED' && id ? (
              <button
                type="button"
                onClick={() => navigate(`/campaigns/${id}/relaunch`)}
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--dash-brand)] px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.04em] text-white uppercase transition hover:opacity-90"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Relaunch
              </button>
            ) : null}
          </div>
        </div>

        {/* Apple-style segmented tabs */}
        <nav
          className="mx-4 mb-4 overflow-x-auto rounded-[14px] bg-[#F2F4F7] p-1 sm:mx-5"
          aria-label="Campaign sections"
        >
          <div className="flex min-w-max gap-1">
            {TABS.map((t) => {
              const active = tab === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-[11px] px-3.5 py-2 text-[13px] font-semibold tracking-[-0.01em] transition ${
                    active
                      ? 'bg-white text-[var(--dash-ink)] shadow-[0_1px_3px_rgba(15,23,42,0.1)]'
                      : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      {tab === 'overview' ? (
        <OverviewTab
          campaign={campaign}
          role={role || ''}
          onSelectTab={setTab}
        />
      ) : null}
      {tab === 'screens' ? (
        <ScreensTab
          campaignId={id}
          startDate={campaign.startDate}
          endDate={campaign.endDate}
          role={role || ''}
        />
      ) : null}
      {tab === 'progress' ? <ProgressTab campaignId={id} /> : null}
      {tab === 'analytics' ? <AnalyticsTab campaignId={id} /> : null}
    </div>
  );
}
